//SvedAoi.js
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useHistory } from 'react-router-dom';
import { AOI_URLS } from '../config/urls';

const AOICard = ({ data, isSelected, onSelect, onDelete }) => {
  // Convert coordinates for the map
  const polygonCoords = data.coordinates.map(coord => [coord.lat, coord.lng]);
  
  // Calculate center of polygon for map focusing
  const getBounds = coords => {
    const lats = coords.map(([lat]) => lat);
    const lngs = coords.map(([, lng]) => lng);
    
    // Calculate the span of the polygon
    const latSpan = Math.max(...lats) - Math.min(...lats);
    const lngSpan = Math.max(...lngs) - Math.min(...lngs);
    
    // Use the larger span to determine zoom
    const maxSpan = Math.max(latSpan, lngSpan);
    
    // Calculate zoom level
    // This is a common formula used in mapping libraries
    // Adjust the constants based on your needs
    const zoom = Math.floor(Math.log2(360 / maxSpan)) - 1;
    
    return {
      center: [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lngs) + Math.max(...lngs)) / 2
      ],
      zoom: Math.min(Math.max(zoom, 1), 18) // Clamp between 1 and 18
    };
  };

  const { center, zoom } = getBounds(polygonCoords);

  return (
    <div 
      onClick={() => onSelect(data)}
      style={{
        backgroundColor: isSelected ? 'rgba(124, 124, 124, 0.4)' : 'transparent',
        border: isSelected ? '2px solid #76b6ff' : '1px solid rgba(255, 251, 251, 0.41)',
        borderRadius: '8px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        paddingLeft:'4px',
        paddingTop:'4px',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
    >
      {/* Map preview */}
      <div style={{ 
        width: '90px', 
        height: '90px', 
        overflow: 'hidden',
        borderRadius: '4px',
        flexShrink: 0
      }}>
        <MapContainer
          center={center}
          zoom={zoom-1}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
          dragging={false}
          touchZoom={false}
          doubleClickZoom={false}
          scrollWheelZoom={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="brightness-50"
          />
          <Polygon
            positions={polygonCoords}
            pathOptions={{
              color: '#76b6ff',
              fillColor: '#76b6ff',
              fillOpacity: 0.4,
              weight: 2
            }}
          />
        </MapContainer>
      </div>

      {/* Info section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: 1
      }}>
        <div style={{ 
          fontSize: '14px', 
          color: '#A6B6CE',
          marginBottom: '4px'
        }}>
          📍 {data.name || `AOI ${data.id}`}
        </div>
        
        <div style={{ 
          fontSize: '12px', 
          color: 'rgba(166, 182, 206, 0.7)' 
        }}>
          📐 {data.area?.toFixed(2)} km²
        </div>

        <div style={{ 
          fontSize: '12px', 
          color: 'rgba(166, 182, 206, 0.7)' 
        }}>
          📅 {new Date(data.created_at).toLocaleDateString()}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(data.id);
          }}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#FF4D4F',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.3s ease',
            alignSelf: 'flex-start',
            marginTop: '4px'
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

const SavedAOIs = ({ drawnCoordinates, onAOISelect }) => {
  const [savedAOIs, setSavedAOIs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
    const history = useHistory();

  useEffect(() => {
    const fetchSavedAOIs = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(AOI_URLS.getSavedAOIs, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 401) {
            alert('Session expired. Please login again.');
            localStorage.removeItem('token'); // Clear the expired token
            history.push('/login');
            return;
          }
        const data = await response.json();
        setSavedAOIs(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch saved AOIs');
        setLoading(false);
      }
    };

    fetchSavedAOIs();
  }, []);

  const handleAOIDelete = async (aoi_id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(AOI_URLS.deleteAOI(aoi_id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      setSavedAOIs(savedAOIs.filter(aoi => aoi.id !== aoi_id));
    } catch (err) {
      console.error('Failed to delete AOI', err);
    }
  };

  const isCurrentAOI = (aoi) => {
    if (!drawnCoordinates || !aoi.coordinates) return false;
    return JSON.stringify(drawnCoordinates) === JSON.stringify(aoi.coordinates);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#A6B6CE'
      }}>
        Loading saved AOIs...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        color: '#FF4D4F',
        padding: '10px',
        textAlign: 'center'
      }}>
        {error}
      </div>
    );
  }

  if (savedAOIs.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#A6B6CE',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '16px' }}>🗺️</div>
        <div>No saved Areas of Interest</div>
        <div style={{ fontSize: '13px', marginTop: '8px', color: 'rgba(166, 182, 206, 0.7)' }}>
          Draw an AOI and save it to see it here
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '16px',
      height: '100%',
      overflowY: 'auto'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '8px',
        rowGap: '16px'
      }}>
        {savedAOIs.map((aoi) => (
          <div key={aoi.id}>
            <AOICard
              data={aoi}
              isSelected={isCurrentAOI(aoi)}
              onSelect={() => onAOISelect(aoi.coordinates, aoi.name || `AOI ${aoi.id}`)}
              onDelete={handleAOIDelete}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedAOIs;